type DateFormatSchema = "DD.MM.YY HH:mm" | "DD.MM.YY" | "ddd MMM DD YYYY";

const dateOnlyRegex = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseIsoDate = (isoString: string, utc: boolean) => {
  const dateOnlyMatch = dateOnlyRegex.exec(isoString);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const yearValue = Number(year);
    const monthIndex = Number(month) - 1;
    const dayValue = Number(day);

    return utc
      ? new Date(Date.UTC(yearValue, monthIndex, dayValue))
      : new Date(yearValue, monthIndex, dayValue);
  }

  return new Date(isoString);
};

const getDatePart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) =>
  parts.find((part) => part.type === type)?.value ?? "";

const getTimeZoneOptions = (utc: boolean) => (utc ? { timeZone: "UTC" } : {});

const formatDayMonthYear = (date: Date, utc: boolean, includeTime: boolean) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    ...(includeTime
      ? {
          hour: "2-digit",
          hourCycle: "h23",
          minute: "2-digit",
        }
      : {}),
    ...getTimeZoneOptions(utc),
  }).formatToParts(date);

  const formattedDate = `${getDatePart(parts, "day")}.${getDatePart(parts, "month")}.${getDatePart(
    parts,
    "year",
  )}`;

  if (!includeTime) {
    return formattedDate;
  }

  return `${formattedDate} ${getDatePart(parts, "hour")}:${getDatePart(parts, "minute")}`;
};

const formatWeekdayMonthDayYear = (date: Date, utc: boolean) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
    ...getTimeZoneOptions(utc),
  }).formatToParts(date);

  return `${getDatePart(parts, "weekday")} ${getDatePart(parts, "month")} ${getDatePart(
    parts,
    "day",
  )} ${getDatePart(parts, "year")}`;
};

export const dateIsoStringToLocaleString = (
  isoString: string,
  schema: DateFormatSchema = "DD.MM.YY HH:mm",
  utc = false,
) => {
  const date = parseIsoDate(isoString, utc);

  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  if (schema === "DD.MM.YY") {
    return formatDayMonthYear(date, utc, false);
  }

  if (schema === "ddd MMM DD YYYY") {
    return formatWeekdayMonthDayYear(date, utc);
  }

  return formatDayMonthYear(date, utc, true);
};
